/**
 * The sales-order record — `/countries/:cc/sales-orders/:soId`. READ-ONLY.
 *
 * =============================================================================
 * THE LINES AND TOTALS HERE ARE A SNAPSHOT, AND THE PAGE SAYS SO
 * =============================================================================
 * `so_lines` and `so_taxes` are an IMMUTABLE COPY taken at confirm — currency, FX
 * rate and every total are snapshots, "not re-derived from the quote afterwards"
 * (`sales-order.entity.ts`). U3's e2e proves them byte-stable against the quote
 * they came from.
 *
 * That is not a detail: it means the quote and the order can legitimately differ
 * later, and a reader comparing the two needs to know which one is the commitment.
 * The properties panel therefore links to the source quote AND states that these
 * figures are frozen — rather than letting somebody assume the order is a live
 * view of the quote.
 *
 * =============================================================================
 * THE TALLY FIELDS ARE EMPTY, AND EMPTY IS AN ANSWER
 * =============================================================================
 * `tally_voucher_no`, `tally_pushed_at`, `invoice_no` and `invoice_date` are all
 * written by the Phase 3b push and the invoice-back that follows it. In this
 * build they are always NULL. They are rendered — with "Not pushed" and "No
 * invoice yet" rather than an em dash — because hiding the rows would hide the
 * gap, and an em dash reads as "unknown" when the truth is "this has not happened
 * and cannot happen yet".
 */

import { Link, useParams } from 'react-router-dom';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { Alert, CountryBadge } from '../../components/ui';
// WT5 — `inquiries/panel` died with the slide-over kill; the property rows
// relocated verbatim to `record-furniture` (import repoint only, no restyle).
import { PropertyGroup, PropertyRow } from '../inquiries/record-furniture';
import { formatDateLong } from '../inquiries/format';
import { SALES_ORDER_STATUS_MAP } from '../../domain/quotes';
import { DocumentLineTable } from './LineTable';
import { TotalsBlock } from './money';
import { DocRail, SalesOrderStatusPill } from './status';
import { DocumentSkeletonRecord } from './states';
import { quoteErrorText } from './errors';
import { useSalesOrderRecord } from './use-quotes';

export function SalesOrderRecordPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { soId } = useParams<{ soId: string }>();
  const { can } = useSession();

  const canView = can(countryCode, 'so.view');
  const canViewQuotes = can(countryCode, 'quotes.view');
  const record = useSalesOrderRecord(soId ?? null, canView);

  if (!canView) {
    return (
      <div className="p-6">
        <Alert tone="danger" title="This order is not available to this account">
          Viewing sales orders in {countryCode} needs the Sales Orders permission.
        </Alert>
      </div>
    );
  }

  if (record.isPending) {
    return (
      <div className="p-6">
        <DocumentSkeletonRecord />
      </div>
    );
  }

  if (record.isError || record.data === undefined) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="This order could not be opened">
          {quoteErrorText(record.error)}
        </Alert>
        <Link
          to={`/countries/${countryCode}/sales-orders`}
          className="w-fit text-body13 text-fg underline underline-offset-2 hover:decoration-2"
        >
          Back to the order list
        </Link>
      </div>
    );
  }

  const order = record.data;
  const descriptor = SALES_ORDER_STATUS_MAP[order.status];
  const pending = order.status === 'pending_push';

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="relative flex items-stretch">
        <DocRail tone={descriptor.tone} />
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <p className="text-label text-fg-secondary">
            <Link to={`/countries/${countryCode}`} className="hover:underline">
              Pipeline
            </Link>
            {' / '}
            <Link to={`/countries/${countryCode}/sales-orders`} className="hover:underline">
              Sales orders
            </Link>
            {' / '}
            <span className="font-mono text-mono">{order.doc_no}</span>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {/* PHASE 3a — record header, same slot as the quote record's. */}
            <CountryBadge code={countryCode} />
            <h1 className="truncate text-h1 text-fg">
              {order.party_name ?? 'Unknown customer'}
            </h1>
            <SalesOrderStatusPill status={order.status} />
          </div>
          {/*
           * NO ACTION CLUSTER. Not because of a permission — because there is no
           * write route. A screen with no buttons and no explanation reads as
           * unfinished, so the sentence below says why it is read-only.
           */}
          {/*
            * ⚠ COPY UPDATED WITH THE DIRECT-ORDER FLOW, and the sentence had to stay
            * true of BOTH paths. It said "created by confirming a quote" — still exact:
            * an order raised through "New sales order" is a quote written and confirmed
            * in one step, not a second kind of order and not a second code path. What is
            * added is where the quote came from, because an operator who never pressed
            * Send will otherwise wonder why a confirmed quote they do not remember
            * sending sits behind their order.
            */}
          <p className="text-label text-fg-secondary">
            Read-only. A sales order is created by confirming a quote — either one that was
            sent to the customer, or one written and confirmed in a single step by New sales
            order — and it has no edit route.
          </p>
        </div>
      </header>

      {pending ? (
        <Alert tone="info" title="Awaiting Tally sync — Phase 3b">
          {/* The brief's own words, and the honest state: no push has been attempted,
            * nothing is running, and this is not an error. */}
          This order has not been pushed to Tally. The write path ships in Phase 3b; no push
          has been attempted and none is in progress. Until then the voucher does not exist
          in Tally and no invoice number can come back.
        </Alert>
      ) : null}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <DocumentLineTable
            caption={`Lines on sales order ${order.doc_no}`}
            lines={order.lines}
            currency={order.currency}
          />
          <TotalsBlock
            totals={{
              subtotal: order.subtotal,
              discount_total: order.discount_total,
              tax_total: order.tax_total,
              grand_total: order.grand_total,
            }}
            taxes={order.taxes}
            currency={order.currency}
          />
          <p className="text-label text-fg-secondary">
            These figures are a snapshot taken when the quote was confirmed. They are not
            re-derived from the quote, so a later change to the quote would not move them.
          </p>
        </div>

        <aside className="flex w-full shrink-0 flex-col gap-5 rounded-md border border-line bg-surface p-5 lg:w-[320px]">
          <PropertyGroup label="Order">
            <PropertyRow name="Number" mono>
              {order.doc_no}
            </PropertyRow>
            <PropertyRow name="Status">{descriptor.label}</PropertyRow>
            <PropertyRow name="Currency">{order.currency}</PropertyRow>
            {/*
              * The tax-group provenance the confirm snapshot copied (migration 30,
              * OD-1 — design U3). Label joined live; the frozen tax statement is
              * the taxes block above. "Not recorded" = confirmed before the cutover.
              */}
            <PropertyRow name="Tax group">
              {order.tax_group_name ?? 'Not recorded'}
            </PropertyRow>
            <PropertyRow name="Created">{formatDateLong(order.created_at)}</PropertyRow>
          </PropertyGroup>

          <PropertyGroup label="Source">
            <PropertyRow name="Customer">{order.party_name ?? '—'}</PropertyRow>
            <PropertyRow name="From quote" mono>
              {canViewQuotes ? (
                <Link
                  to={`/countries/${countryCode}/quotes/${order.quote_id}`}
                  className="text-fg underline underline-offset-2 hover:decoration-2"
                >
                  {order.quote_doc_no ?? order.quote_id}
                </Link>
              ) : (
                (order.quote_doc_no ?? '—')
              )}
            </PropertyRow>
          </PropertyGroup>

          <PropertyGroup label="Tally">
            {/* Phase 3b writes all four. Rendered rather than hidden — see the header. */}
            <PropertyRow name="Voucher no" mono>
              {order.tally_voucher_no ?? 'Not pushed'}
            </PropertyRow>
            <PropertyRow name="Pushed at">
              {order.tally_pushed_at === null
                ? 'Not pushed'
                : formatDateLong(order.tally_pushed_at)}
            </PropertyRow>
            <PropertyRow name="Invoice no" mono>
              {order.invoice_no ?? 'No invoice yet'}
            </PropertyRow>
            <PropertyRow name="Invoice date">
              {order.invoice_date === null ? 'No invoice yet' : formatDateLong(order.invoice_date)}
            </PropertyRow>
          </PropertyGroup>
        </aside>
      </div>
    </div>
  );
}
